<template>
  <b-container fluid>
    <brands-filter
      :panel.sync="activePanel"
      :filter-active="(selBrands.length > 0 && selBrands.length < elevatorBrands.length) || (selModelTypes.length > 0 && selModelTypes.length < elevatorModelTypes.length)"
      :brands="elevatorBrands"
      :selected-brands.sync="selectedBrands"
      :model-types="elevatorModelTypes"
      :selected-model-types.sync="selectedModelTypes"
      name-prefix="events"
      @invert="invertBrands()"
      @all="onBrandsAll()"
    >
      <template #buttons>
        <b-button size="sm" @click="activePanel = activePanel === 'types' ? null : 'types'"
          variant="primary">Events</b-button>
      </template>
      <template #search>
        <clearable-input v-model="textFilter" placeholder="Filter..."></clearable-input>
      </template>
      <template #extra-panel>
        <div v-show="activePanel === 'types'" class="filter-panel-content">
          <b-row>
            <b-col sm="2">
              <b-form-group align="left">
                <b-form-checkbox-group v-model="selectedEventTypes" :options="eventTypeLabels" name="events-type-select">
                </b-form-checkbox-group>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-button size="sm" variant="secondary" @click="selectedEventTypes = eventTypeLabels">All</b-button>
              <b-button size="sm" variant="secondary" @click="invertTypes()">Invert</b-button>
            </b-col>
          </b-row>
        </div>
      </template>
    </brands-filter>

    <b-row class="mt-3">
      <b-col>
        <h2>Events</h2>
      </b-col>
    </b-row>

    <b-row align-v="center" class="mt-3">
      <b-col sm="2" style="min-width: fit-content">
        From: <date-picker :value="fromDate" @change="setFrom" />
      </b-col>

      <b-col sm="2" style="min-width: fit-content">
        To: <date-picker :value="toDate" @change="setTo" />
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col>
        <b-button size="sm" class="mt-1" @click="setRange('today')">Today</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('yesterday')">Yesterday</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisWeek')">This Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastWeek')">Last Week</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisMonth')">This Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastMonth')">Last Month</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('thisYear')">This Year</b-button>
        <b-button size="sm" class="mt-1" @click="setRange('lastYear')">Last Year</b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="mt-3">
        <b-table id="events" striped :items="events" :fields="fields" @row-clicked="rowClickedHandler"
          :per-page="perPage" :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
          @sort-changed="onSortChanged" :busy="!loaded">
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner small /> Loading&hellip;
            </div>
          </template>
          <template #cell(elevator_name)="{ item }">
            <router-link :to="'/lifts/' + item.elevator_id" class="row-link">{{ item.elevator_name }}</router-link>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mt-2 align-items-center">
      <b-col cols="auto" v-if="events.length > 15">
        <b-form-select v-model="perPage" :options="perPageOptions" style="width: auto"></b-form-select>
      </b-col>
      <b-col v-if="events.length > perPage">
        <b-pagination v-model="currentPage" :total-rows="events.length" :per-page="perPage" aria-controls="events"
          class="mb-0"></b-pagination>
      </b-col>
    </b-row>

  </b-container>
</template>

<script>
import api from './api.js'
import auth from './auth.js'
import moment from 'moment';
import formatter from './formatter.mixin.js';
import DatePicker from './DatePicker.vue'

import ClearableInput from './ClearableInput.vue'
import BrandsFilter from './BrandsFilter.vue'
import brandFilterMixin from './brandFilter.mixin.js'

export default {
  name: 'events',
  props: ['elevators', 'settings'],
  mixins: [formatter, brandFilterMixin],
  components: { DatePicker, ClearableInput, BrandsFilter },
  data() {
    const storedTypes = localStorage.getItem('eventsTypes');
    return {
      currentPage: 1,
      perPage: parseInt(localStorage.getItem('eventsPerPage')) || 15,
      perPageOptions: [
        { value: 15, text: '15' },
        { value: 30, text: '30' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      sortBy: localStorage.getItem('eventsSortBy') || 'time',
      sortDesc: localStorage.getItem('eventsSortDesc') !== 'false',
      selEventTypes: storedTypes ? JSON.parse(storedTypes) : [],
      textFilter: (window.history.state && window.history.state.textFilter) || '',
      activePanel: null,
      loggedIn: auth.loggedIn(),
      loaded: false,
      events: [],
      cachedElevatorTypes: [],
      cachedEventTypes: [],
      localFrom: null,
      localTo: null,
    }
  },
  computed: {
    fromDate() { return this.from ? moment(this.from).format('YYYY-MM-DD') : '' },
    toDate() { return this.to ? moment(this.to).format('YYYY-MM-DD') : '' },
    fields() {
      const cols = [
        { key: 'elevator_name', sortable: true, label: 'Lift', formatter: v => v },
        { key: 'time', sortable: true, formatter: this.eventTimeFormatter },
      ];
      cols.push({ key: 'type', sortable: true, label: 'Event', formatter: this.eventTypeFormatter });
      cols.push({
        key: 'msg', sortable: true, label: 'Description',
        formatter: (value, key, item) => this.eventDescriptionForType(item.type, value),
      });
      return cols;
    },
    elevatorBrands() {
      return [...new Set((this.elevators || []).map(e => this.elevatorBrandFormatter(e.type)))].filter(Boolean).sort();
    },
    elevatorModelTypes() {
      return [...new Set((this.elevators || [])
        .filter(e => this.selectedBrands.includes(this.elevatorBrandFormatter(e.type)))
        .map(e => this.elevatorTypeFormatter(e.type) || 'Other'))].filter(Boolean).sort();
    },
    eventTypeLabels() {
      return [...new Set(this.cachedEventTypes.map(t => this.eventTypeFormatter(t)))].sort();
    },
    selectedBrands: {
      get() { return this.selBrands.length > 0 ? this.selBrands : this.elevatorBrands },
      set(v) {
        const effective = v.length < this.elevatorBrands.length ? v : []
        if (effective.length === 0 && this.selBrands.length === 0) return
        this.selBrands = effective
        this.selModelTypes = []
      },
    },
    selectedModelTypes: {
      get() { return this.selModelTypes.length > 0 ? this.selModelTypes : this.elevatorModelTypes },
      set(v) {
        const effective = v.length < this.elevatorModelTypes.length ? v : []
        if (effective.length === 0 && this.selModelTypes.length === 0) return
        this.selModelTypes = effective
      },
    },
    selectedEventTypes: {
      get() {
        return this.selEventTypes.length > 0 ? this.selEventTypes : this.eventTypeLabels;
      },
      set(newTypes) {
        const effective = newTypes.length < this.eventTypeLabels.length ? newTypes : [];
        if (effective.length === 0 && this.selEventTypes.length === 0) return;
        if (effective.length === this.selEventTypes.length && effective.every((x, i) => x === this.selEventTypes[i])) return;
        this.selEventTypes = effective;
        if (effective.length > 0) {
          localStorage.setItem('eventsTypes', JSON.stringify(effective));
        } else {
          localStorage.removeItem('eventsTypes');
        }
      }
    },
    from: {
      get() { return this.localFrom; },
      set(v) {
        this.localFrom = v;
      }
    },
    to: {
      get() { return this.localTo; },
      set(v) {
        this.localTo = v;
      }
    },
    // Resolve selected label-based event type filters back to raw event type values.
    selectedRawEventTypes() {
      if (this.selEventTypes.length === 0) return [];
      const reverseMap = {
        'Reset': ['reset'],
        'Alarm': ['alarm'],
        'Restart': ['restart'],
        'Connect': ['connect'],
        'Fault': ['error'],
        'Breakdown': ['outage'],
        'Breakdown ended': ['outage-end', 'outage-resolved'],
        'Maintenance': ['service'],
        'Maintenance ended': ['service-end'],
        'Monitoring enabled': ['repair-end'],
        'Mode': ['mode'],
        'Power restored': ['power-on'],
        'Power lost': ['power-off'],
        'Water level': ['waterlevel'],
      };
      const raw = [];
      for (const label of this.selEventTypes) {
        const types = reverseMap[label] || [label];
        for (const t of types) {
          if (!raw.includes(t)) raw.push(t);
        }
      }
      return raw;
    },
    // Resolve selected brand+model filters to raw elevator_type keys.
    selectedElevatorTypes() {
      if (this.selBrands.length === 0 && this.selModelTypes.length === 0) return [];
      return Object.keys(this.ElevatorTypes).filter(rawType => {
        const brand = this.elevatorBrandFormatter(rawType);
        if (!this.selectedBrands.includes(brand)) return false;
        const model = this.elevatorTypeFormatter(rawType) || 'Other';
        return this.selectedModelTypes.includes(model);
      });
    },
  },
  watch: {
    perPage(v) { localStorage.setItem('eventsPerPage', String(v)); this.currentPage = 1; },
    selEventTypes() { if (this._initialized) this._reloadEvents(); },
    selBrands() { if (this._initialized) this._reloadEvents(); },
    selModelTypes() { if (this._initialized) this._reloadEvents(); },
    textFilter() {
      if (!this._initialized) return;
      const state = Object.assign({}, window.history.state, { textFilter: this.textFilter });
      window.history.replaceState(state, '');
      clearTimeout(this._textFilterTimer);
      this._textFilterTimer = setTimeout(() => this._reloadEvents(), 500);
    },
  },
  methods: {
    setFrom(v) {
      this.from = v ? moment(v).startOf('day').format() : null;
      this._reloadEvents();
    },
    setTo(v) {
      this.to = v ? moment(v).endOf('day').format() : null;
      this._reloadEvents();
    },
    _reloadEvents() {
      this.currentPage = 1;
      this.getEvents();
    },
    onSortChanged({ sortBy, sortDesc }) {
      localStorage.setItem('eventsSortBy', sortBy);
      localStorage.setItem('eventsSortDesc', sortDesc);
    },
    invertTypes() {
      this.selectedEventTypes = this.eventTypeLabels.filter(x => !this.selEventTypes.includes(x));
    },
    rowClickedHandler(record, index) {
      this.$router.push('/lifts/' + record.elevator_id)
    },
    getEvents() {
      const PAGE_SIZE = 1000;
      this.currentPage = 1;
      this.loaded = false;

      const fetchId = ++this._fetchId;
      const filters = {
        eventTypes: this.selectedRawEventTypes,
        elevatorTypes: this.selectedElevatorTypes,
        text: this.textFilter,
      };

      const drain = (cursor, acc) => {
        api.getEvents(this.from, this.to, cursor, PAGE_SIZE, filters, resp => {
          if (fetchId !== this._fetchId) return;
          if (!resp.data) {
            this.events = acc;
            this.loaded = true;
            if (!this._initialized) this.$nextTick(() => { this._initialized = true; });
            return;
          }
          const page = resp.data.data || [];
          const all = acc.concat(page);
          if (resp.data.has_more && resp.data.next_cursor) {
            drain(resp.data.next_cursor, all);
          } else {
            this.events = all;
            const newTypes = all.map(e => e.elevator_type);
            const newEventTypes = all.map(e => e.type).filter(Boolean);
            this.cachedElevatorTypes = [...new Set([...this.cachedElevatorTypes, ...newTypes])];
            this.cachedEventTypes = [...new Set([...this.cachedEventTypes, ...newEventTypes])];
            this.loaded = true;
            if (!this._initialized) this.$nextTick(() => { this._initialized = true; });
          }
        });
      };

      drain(null, []);
    },
    setRange(range) {
      switch (range) {
        case 'today':
          this.from = moment().startOf('day').format();
          this.to = moment().endOf('day').format();
          break;
        case 'yesterday':
          this.from = moment().startOf('day').subtract(1, 'day').format();
          this.to = moment().endOf('day').subtract(1, 'day').format();
          break;
        case 'thisWeek':
          this.from = moment().startOf('week').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastWeek':
          this.from = moment().startOf('week').subtract(1, 'week').format();
          this.to = moment().endOf('week').subtract(1, 'week').format();
          break;
        case 'thisMonth':
          this.from = moment().startOf('month').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastMonth':
          this.from = moment().startOf('month').subtract(1, 'month').format();
          this.to = moment().subtract(1, 'month').endOf('month').format();
          break;
        case 'thisYear':
          this.from = moment().startOf('year').format();
          this.to = moment().endOf('day').format();
          break;
        case 'lastYear':
          this.from = moment().startOf('year').subtract(1, 'year').format();
          this.to = moment().endOf('year').subtract(1, 'year').format();
          break;
        case 'all':
          this.from = null;
          this.to = null;
          break;
      }

      this.currentPage = 1;
      this.getEvents();
    },
    handleEventUpdate(event) {
      const t = new Date(event.time);
      const from = this.from ? new Date(this.from) : null;
      const to = this.to ? new Date(this.to) : null;
      if (from && t < from) return;
      if (to && t > to) return;
      this.events = [event, ...this.events];
      if (event.elevator_type && !this.cachedElevatorTypes.includes(event.elevator_type)) {
        this.cachedElevatorTypes = [...this.cachedElevatorTypes, event.elevator_type];
      }
      if (event.type && !this.cachedEventTypes.includes(event.type)) {
        this.cachedEventTypes = [...this.cachedEventTypes, event.type];
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    history.replaceState(Object.assign({}, history.state, { eventsFrom: this.from, eventsTo: this.to }), '');
    next();
  },
  created() {
    this._fetchId = 0;
    this._textFilterTimer = null;
    this._initialized = false;
    auth.onChange = loggedIn => {
      this.loggedIn = loggedIn;
    }

    const state = history.state || {};
    if (state.eventsFrom) {
      this.from = state.eventsFrom;
      this.to = state.eventsTo;
    } else {
      this.from = moment().startOf('day').format();
      this.to = moment().endOf('day').format();
    }

    this.getEvents();
    this.$root.$on('elevator-event', this.handleEventUpdate);
  },
  beforeDestroy() {
    this.$root.$off('elevator-event', this.handleEventUpdate);
  },
}
</script>

<style>
#events tr {
  cursor: pointer;
}

#events .row-link {
  color: inherit;
  text-decoration: none;
}

.filter-panel {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.35s ease;
}

.filter-panel-open {
  max-height: 1000px;
}

.filter-panel-content {
  border: 1px solid #dee2e6;
  border-radius: 0 0.25rem 0.25rem 0.25rem;
  padding: 0.75rem 1rem 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/Events.vue